import { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Check, Trash2 } from "lucide-react";
import api from "../../lib/api";
import { photoUrl } from "./InstagramConnect";

export function UploadZone({ locations, tags, onDone }) {
  const input = useRef(null);
  const [loc, setLoc] = useState("");
  const [tag, setTag] = useState("");
  const [prog, setProg] = useState(null);
  const upload = async (files) => {
    const list = [...files].filter((f) => f.type.startsWith("image/"));
    if (!list.length) return;
    let ok = 0;
    for (let i = 0; i < list.length; i++) {
      setProg({ done: i, total: list.length });
      const fd = new FormData();
      fd.append("file", list[i]); fd.append("location", loc); fd.append("tags", tag);
      try { await api.post("/social/photos", fd); ok++; } catch (e) { toast.error(`${list[i].name}: ${e?.response?.data?.detail || "upload failed"}`); }
    }
    setProg(null);
    toast.success(`Uploaded ${ok} photo${ok === 1 ? "" : "s"}`);
    onDone();
  };
  return (
    <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }}
      className="rounded-xl border-2 border-dashed border-brand-borderStrong bg-brand-surface p-6 flex flex-wrap items-center gap-4" data-testid="social-upload-zone">
      <UploadCloud className="h-8 w-8 text-brand-gold" />
      <div className="flex-1 min-w-[200px]">
        <p className="text-sm text-brand-ink font-medium">{prog ? `Uploading ${prog.done + 1} of ${prog.total}…` : "Drop photos here, or choose files"}</p>
        <p className="text-xs text-brand-muted">JPG, PNG or HEIC. We auto-fit them to Instagram's shape. Tag them so AI and filters can use them.</p>
        {prog && <div className="h-1.5 mt-2 rounded-full bg-brand-hover overflow-hidden"><div className="h-full bg-brand-gold transition-all" style={{ width: `${(prog.done / prog.total) * 100}%` }} /></div>}
      </div>
      <select value={loc} onChange={(e) => setLoc(e.target.value)} data-testid="social-upload-location" className="text-sm rounded-lg border border-brand-border bg-brand-page px-3 py-2">
        <option value="">Location (optional)</option>{locations.map((l) => <option key={l}>{l}</option>)}
      </select>
      <select value={tag} onChange={(e) => setTag(e.target.value)} data-testid="social-upload-tag" className="text-sm rounded-lg border border-brand-border bg-brand-page px-3 py-2">
        <option value="">Theme (optional)</option>{tags.map((t) => <option key={t}>{t}</option>)}
      </select>
      <button onClick={() => input.current?.click()} disabled={!!prog} data-testid="social-upload-btn" className="rounded-full bg-brand-ink text-brand-page px-4 py-2 text-sm disabled:opacity-50">Choose photos</button>
      <input ref={input} type="file" accept="image/*" multiple hidden onChange={(e) => upload(e.target.files)} data-testid="social-upload-input" />
    </div>
  );
}

export function PhotoGrid({ photos, selected, onToggle, onDelete }) {
  if (!photos.length) return <p className="text-sm text-brand-muted py-10 text-center" data-testid="social-library-empty">No photos yet — upload a batch above.</p>;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="social-photo-grid">
      {photos.map((p) => {
        const idx = selected.indexOf(p.id);
        return (
          <div key={p.id} className={`group relative aspect-[4/5] rounded-lg overflow-hidden border-2 cursor-pointer transition-all ${idx >= 0 ? "border-brand-gold" : "border-transparent hover:border-brand-border"}`}
            onClick={() => onToggle(p.id)} data-testid={`social-photo-${p.id}`}>
            <img src={photoUrl(p.id, true)} alt={p.filename} loading="lazy" className="h-full w-full object-cover" />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 text-[10px] text-white">
              {p.location || "No location"}{p.tags?.length ? ` · ${p.tags.join(", ")}` : ""}
            </div>
            {p.used_count > 0 && <span className="absolute top-2 left-2 rounded-full bg-black/60 text-white text-[10px] px-2 py-0.5">Used {p.used_count}×</span>}
            {idx >= 0 && <span className="absolute top-2 right-2 h-6 w-6 rounded-full bg-brand-gold text-white text-xs flex items-center justify-center">{idx + 1}</span>}
            {idx < 0 && <button onClick={(e) => { e.stopPropagation(); onDelete(p.id); }} data-testid={`social-photo-delete-${p.id}`}
              className="absolute top-2 right-2 h-6 w-6 rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"><Trash2 className="h-3 w-3" /></button>}
            {idx >= 0 && <Check className="absolute inset-0 m-auto h-8 w-8 text-white drop-shadow" />}
          </div>
        );
      })}
    </div>
  );
}
